import { documentSignsService } from '@/services';
import { ISignCommand } from '@/sign/commands/isign.command';
import { SigningParamsContainer } from '@/sign/models/signing-params.model';
import { AuthorSignDocumentStrategyFactory } from '../factories/author-sign-document-strategy.factory';
import {
  showLoading, hideLoading, isLoadingVisible, disableShowMsg, enableShowMsg, setLoadingProgress, setLoadingText
} from '@/mixins/ui-mixin';
import { showConfirm } from '@/plugins/dialogs';
import { BaseSignDocumentCommand, ICommandSigningResult } from './sign-document.command';
import { DocumentSignVisualizationHelper } from '@/helpers/document-sign-visualization.helper';
import { getLocale } from '@/helpers';
import { FileSigningResult } from '@/models/document-sign.models';

export class SignDocumentAuthorCommand extends BaseSignDocumentCommand implements ISignCommand {
  constructor(signStrategyParams: any) {
    super(signStrategyParams, new AuthorSignDocumentStrategyFactory());
    this.isSealAllowed = true;
  }

  public async execute(signMethodParams: SigningParamsContainer): Promise<any> {
    disableShowMsg();
    try {
      const ret = (await super.execute(signMethodParams)) as ICommandSigningResult;

      const visHelper = new DocumentSignVisualizationHelper();

      const checkResult = await this.checkSignedHashes(ret.keySigningResult, ret.sealSigningResult);

      setLoadingText({ text: getLocale('appLoading.saveDocumentSignatures').toString() });
      const saveResult = await this.saveSignes(checkResult);
      setLoadingText({ text: '' });

      if (saveResult) {
        let files = checkResult.signedHashes.map((sh: any) => sh.pDocumentVersionsFileCode);
        setLoadingText({ text: getLocale('appLoading.createDocumentFileVisualization').toString() });
        await visHelper.update(this.signStrategyParams.documentVersionCode, files, (total, current) => {
          setLoadingProgress({ total, current, showTotal: true });
        });
        setLoadingText({ text: '' });
      }

      return saveResult;
    } finally {
      enableShowMsg();
    }
  }

  private async saveSignes({ signedHashes, stampSignedHashes, isConfirmed, checkResult }): Promise<any> {
    if (isConfirmed) {
      let replace = false;
      if (checkResult && checkResult.pResult === 2) {
        replace = true;
      }
      try {
        await documentSignsService.saveAuthorSignatures(signedHashes, stampSignedHashes, replace);
      } catch (err) {
        await this.handleSignSavingError(err);
      }
      return true;
    } else {
      return false;
    }
  }

  private async checkSignedHashes(signedHashes: Array<FileSigningResult>,
    stampSignedHashes: Array<FileSigningResult> | null): Promise<any> {
    let signedHashesToCheck = signedHashes.filter(sh => !sh.isInternalNested);
    let stampSignedHashesToCheck = stampSignedHashes ? stampSignedHashes.filter(sh => !sh.isInternalNested) : null;

    let isConfirmed = true;
    let checkResult: any;

    if (signedHashesToCheck.length > 0) {
      checkResult = await documentSignsService.checkAuthorSignsSerials(signedHashesToCheck, stampSignedHashesToCheck);

      if (checkResult && checkResult.pResult === 2) {
        const showLoader = isLoadingVisible();
        if (showLoader) {
          await hideLoading();
        }
        isConfirmed = await showConfirm('appSign.autorExistQuestoion');
        if (showLoader) {
          showLoading({}, true);
        }
      }
    } else {
      checkResult = { pResult: 1 };
    }

    return { signedHashes, stampSignedHashes, checkResult, isConfirmed };
  }
}
